import { memo } from "react";
import type { CSSProperties, FunctionComponent } from "react";
import { Box } from "../box";
import { Group } from "../group";
import { Progress } from "../progress";
import { componentWrapper } from "../../utilities";
import { DEFAULT_MARKER_COLOR } from "./progress-chart.constants";
import styles from "./progress-chart.module.scss";
import type { CustomProgressChartFactory, ILegendProps, IProps } from "./progress-chart.types";
import { clampPercentage } from "./progress-chart.utilities";

/**
 * Progress bar chart with an optional positional marker dot, for showing progress through an
 * allowance against a second reference point such as progress through a period.
 * @param props.value Fill percentage, clamped to 0-100.
 * @param props.markerValue Marker dot position percentage; hidden when undefined; clamped to 0-100.
 * @param props.markerColor Marker dot colour; defaults to the warning surface token.
 * @returns The ProgressChart component.
 */
const ProgressChart = componentWrapper<CustomProgressChartFactory, IProps>(
    "ProgressChart",
    ({ markerColor = DEFAULT_MARKER_COLOR, markerValue, value = 0, ...otherProps }, ref) => {
        return (
            <Box pos="relative" w="100%">
                <Progress
                    classNames={{ section: styles["section"] }}
                    ref={ref}
                    value={clampPercentage(value)}
                    {...otherProps}
                />
                {typeof markerValue !== "undefined" && (
                    <span
                        className={styles["marker"]}
                        data-testid="progress-chart-marker"
                        style={
                            {
                                "--progress-chart-marker-color": markerColor,
                                "--progress-chart-marker-left": `${clampPercentage(markerValue)}%`,
                            } as CSSProperties
                        }
                    />
                )}
            </Box>
        );
    }
);

/**
 * Legend row for a ProgressChart: coloured dots with labels, plus optional trailing content such
 * as a badge.
 * @param props.items Legend entries rendered as a coloured dot and label, in order.
 * @param props.children Trailing content appended after the items.
 * @returns The ProgressChart legend row.
 */
const ProgressChartLegendBase: FunctionComponent<ILegendProps> = ({ children, items }) => {
    return (
        <Group data-testid="progress-chart-legend" gap="md" wrap="wrap">
            {items.map((item, index) => (
                <Group gap="sm" key={`${item.color}-${index}`}>
                    <span
                        className={styles["legend-dot"]}
                        data-testid="progress-chart-legend-dot"
                        style={{ "--progress-chart-legend-dot-color": item.color } as CSSProperties}
                    />
                    {item.label}
                </Group>
            ))}
            {children}
        </Group>
    );
};

export const ProgressChartLegend = memo(ProgressChartLegendBase);

// componentWrapper only types staticComponents; they must be attached explicitly.
ProgressChart.Legend = ProgressChartLegend;

export { ProgressChart };
